import React, { useEffect, useState, useCallback } from 'react';
import { posts, ai, scheduler, integrations } from '../lib/api';
import { Eye, Sparkles, Wand2, CalendarDays, Check, Trash2, Camera, Edit3, Image as ImageIcon, MessageSquare, Clock, Send, Play, MessageCircle, Briefcase, Users } from 'lucide-react';

const PLATFORMS = ['instagram','twitter','linkedin','facebook'];

const platformIcon = (p, c) => {
  const props = {size:20, strokeWidth:2.5, color:c||'#222'};
  if(p==='instagram') return <Camera {...props}/>;
  if(p==='twitter') return <MessageCircle {...props}/>;
  if(p==='linkedin') return <Briefcase {...props}/>;
  if(p==='facebook') return <Users {...props}/>;
  return null;
};

export default function AttentionPage() {
  const [view, setView] = useState('compose'); // compose | calendar | queue | analytics
  const [postList, setPostList] = useState([]);
  const [queueList, setQueueList] = useState([]);
  const [connected, setConnected] = useState([]);
  const [bestTimes, setBestTimes] = useState({});
  const [loading, setLoading] = useState(false);

  // Compose state
  const [compose, setCompose] = useState({ content:'', platform:'instagram', scheduled_at:'' });
  const [aiLoading, setAiLoading] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiTone, setAiTone] = useState('engaging');
  const [saveMsg, setSaveMsg] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [p, q, intgs, bt] = await Promise.all([
        posts.list({ limit: 20 }).catch(() => []),
        scheduler.queue().catch(() => []),
        integrations.list().catch(() => []),
        scheduler.bestTimes().catch(() => ({})),
      ]);
      setPostList(Array.isArray(p) ? p : []);
      setQueueList(Array.isArray(q) ? q : []);
      setConnected(Array.isArray(intgs) ? intgs.filter(i => i.is_active).map(i => i.platform) : []);
      setBestTimes(bt && typeof bt === 'object' ? bt : {});
    } catch(e){ console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const generateWithAI = async () => {
    if (!aiTopic.trim()) return;
    setAiLoading(true);
    try {
      const res = await ai.generatePost({ topic: aiTopic, platform: compose.platform, tone: aiTone });
      setCompose(c => ({ ...c, content: res.content }));
    } catch(e) { alert('AI generation failed: ' + (e.response?.data?.error || e.message)); }
    setAiLoading(false);
  };

  const improveWithAI = async () => {
    if (!compose.content.trim()) return;
    setAiLoading(true);
    try {
      const res = await ai.improvePost({ content: compose.content, platform: compose.platform });
      setCompose(c => ({ ...c, content: res.improved }));
    } catch(e) { alert('Improvement failed: ' + (e.response?.data?.error || e.message)); }
    setAiLoading(false);
  };

  const saveAsDraft = async () => {
    if (!compose.content.trim()) return;
    try {
      await posts.create({ ...compose, status:'draft', scheduled_at: null });
      setSaveMsg('Saved as draft ✓');
      setTimeout(() => setSaveMsg(''), 2500);
      loadData();
    } catch(e) { alert(e.response?.data?.error || 'Save failed'); }
  };

  const schedulePost = async () => {
    if (!compose.content.trim() || !compose.scheduled_at) {
      alert('Add content and pick a schedule time');
      return;
    }
    try {
      const ts = Math.floor(new Date(compose.scheduled_at).getTime() / 1000);
      await posts.create({ ...compose, scheduled_at: ts });
      setSaveMsg('Scheduled ✓');
      setTimeout(() => setSaveMsg(''), 2500);
      setCompose({ content:'', platform:'instagram', scheduled_at:'' });
      loadData();
    } catch(e) { alert(e.response?.data?.error || 'Schedule failed'); }
  };

  const publishNow = async (id) => {
    if (!window.confirm('Publish this post now?')) return;
    try {
      await posts.publish(id);
      setSaveMsg('Published ✓');
      setTimeout(() => setSaveMsg(''), 2500);
      loadData();
    } catch(e) { alert(e.response?.data?.error || 'Publish failed'); }
  };

  const deletePost = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try { await posts.delete(id); loadData(); } catch(e) { alert('Delete failed'); }
  };

  const charLimit = { instagram:2200, twitter:280, linkedin:3000, facebook:63206 };
  const remaining = charLimit[compose.platform] - compose.content.length;

  return (
    <div className="page-enter">
      {/* Topbar */}
      <div style={s.topbar}>
        <div>
          <div style={s.pageTitle}><Eye size={24} strokeWidth={3} style={{verticalAlign:'-3px'}}/> Attention Engine</div>
          <div style={s.pageSub}>Schedule, create, and publish content across platforms</div>
        </div>
        <div style={s.topbarRight}>
          {connected.length > 0
            ? connected.map(p => <span key={p} style={{...s.connectedBadge, background:platformColor(p), color:'#FFF'}}>{p}</span>)
            : <span style={s.warnBadge}>No platforms connected — go to Settings</span>
          }
        </div>
      </div>

      {/* Tabs */}
      <div style={s.tabs}>
        {[
          {id:'compose', label:'Compose', icon:<Edit3 size={16} strokeWidth={2.5}/>},
          {id:'queue', label:'Queue', icon:<CalendarDays size={16} strokeWidth={2.5}/>},
          {id:'posts', label:'All Posts', icon:<MessageSquare size={16} strokeWidth={2.5}/>},
          {id:'best-times', label:'Best Times', icon:<Clock size={16} strokeWidth={2.5}/>}
        ].map(t => (
          <button key={t.id} onClick={() => setView(t.id)} style={{...s.tab, ...(view===t.id?s.tabActive:{})}}>
            {t.icon} {t.label}
          </button>
        ))}
        {saveMsg && <span style={s.saveMsg}>{saveMsg}</span>}
      </div>

      <div style={s.content}>

        {/* COMPOSE TAB */}
        {view === 'compose' && (
          <div style={s.composeGrid}>
            {/* Left: AI + Editor */}
            <div>
              {/* AI Generator */}
              <div style={{...s.card, background:'#E8F2FF'}}>
                <div style={s.cardTitle}><Sparkles size={20} strokeWidth={2.5} color="#118AB2"/> AI Content Generator</div>
                <div style={s.row}>
                  <div style={{flex:1}}>
                    <label style={s.label}>Topic or idea</label>
                    <input value={aiTopic} onChange={e=>setAiTopic(e.target.value)}
                      placeholder="e.g. 'Summer sale announcement'..."
                      style={s.input} onKeyDown={e=>e.key==='Enter'&&generateWithAI()} />
                  </div>
                  <div>
                    <label style={s.label}>Tone</label>
                    <select value={aiTone} onChange={e=>setAiTone(e.target.value)} style={s.select}>
                      {['engaging','professional','funny','inspirational','casual','urgent'].map(t=>(
                         <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <button onClick={generateWithAI} disabled={aiLoading||!aiTopic.trim()} style={s.aiBtn}>
                  {aiLoading ? 'Generating…' : <><Wand2 size={16} strokeWidth={2.5} /> Generate with AI</>}
                </button>
              </div>

              {/* Editor */}
              <div style={s.card}>
                <div style={s.cardTitle}>Post Editor</div>
                {/* Platform selector */}
                <div style={s.platformRow}>
                  {PLATFORMS.map(p => (
                    <button key={p} onClick={() => setCompose(c=>({...c,platform:p}))}
                      style={{...s.platformBtn, ...(compose.platform===p ? {background:platformColor(p), borderColor:'#222', color:'#FFF'} : {})}}>
                       {platformIcon(p)} {p}
                    </button>
                  ))}
                </div>

                <textarea
                  value={compose.content}
                  onChange={e=>setCompose(c=>({...c,content:e.target.value}))}
                  placeholder={`Write your ${compose.platform} post here, or use AI…`}
                  style={{...s.textarea, borderColor: remaining < 0 ? '#EF476F' : '#222'}}
                  rows={7}
                />
                <div style={s.charRow}>
                  <button onClick={improveWithAI} disabled={aiLoading||!compose.content.trim()} style={s.improveBtn}>
                    <Wand2 size={14} strokeWidth={2.5}/> Improve
                  </button>
                  <span style={{color: remaining < 20 ? '#EF476F' : '#666', fontSize:13, fontWeight:700, fontFamily:'"Nunito",sans-serif'}}>
                    {remaining} chars
                  </span>
                </div>

                {/* Schedule time */}
                <div style={{marginTop:20}}>
                  <label style={s.label}>Schedule for (optional)</label>
                  <input type="datetime-local" value={compose.scheduled_at}
                    onChange={e=>setCompose(c=>({...c,scheduled_at:e.target.value}))}
                    style={{...s.input, maxWidth:240}} />
                </div>

                {/* Actions */}
                <div style={s.actionRow}>
                  <button onClick={saveAsDraft} style={s.draftBtn}>Save Draft</button>
                  <button onClick={schedulePost} disabled={!compose.content.trim()||!compose.scheduled_at} style={s.scheduleBtn}>
                    <Send size={16} strokeWidth={2.5}/> Schedule Post
                  </button>
                </div>
              </div>
            </div>

            {/* Right: Preview */}
            <div>
              <div style={s.card}>
                <div style={s.cardTitle}>Preview</div>
                <div style={s.preview}>
                  <div style={s.previewHeader}>
                    <div style={s.previewAvatar}>M</div>
                    <div>
                      <div style={{fontSize:14,fontFamily:'"Fredoka",sans-serif',fontWeight:700,color:'#222'}}>Your Brand</div>
                      <div style={{fontSize:11,color:'#666',fontWeight:700}}>{compose.platform}</div>
                    </div>
                    <div style={{marginLeft:'auto'}}>{platformIcon(compose.platform, '#222')}</div>
                  </div>
                  <div style={s.previewContent}>
                    {compose.content || <span style={{color:'#888'}}>Your post will preview here…</span>}
                  </div>
                  {compose.scheduled_at && (
                    <div style={s.previewTime}><Clock size={12} strokeWidth={2.5} style={{verticalAlign:'-1px'}}/> Scheduled: {new Date(compose.scheduled_at).toLocaleString('en-IN')}</div>
                  )}
                </div>
              </div>

              {/* Quick ideas */}
              <div style={{...s.card, marginTop:20, background:'#FFF9EB'}}>
                <div style={s.cardTitle}>💡 Quick Ideas</div>
                {['Share a behind-the-scenes moment','Post a customer success story','Share an industry tip or insight','Ask your audience a question','Announce a product update'].map((idea,i) => (
                  <div key={i} onClick={()=>setAiTopic(idea)} style={s.ideaItem}>
                    {idea}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* QUEUE TAB */}
        {view === 'queue' && (
          <div style={s.card}>
            <div style={s.cardTitle}><CalendarDays size={20} strokeWidth={2.5}/> Scheduled Posts ({queueList.length})</div>
            {queueList.length === 0 && <EmptyState icon={<CalendarDays size={40}/>} text="No posts in queue. Schedule one from the Compose tab." />}
            {queueList.map(p => (
              <div key={p.id} style={s.postRow}>
                <div style={{...s.platformDot, background:platformColor(p.platform)}} />
                <div style={{flex:1,minWidth:0}}>
                  <div style={s.postContent}>{p.content.slice(0,120)}{p.content.length>120?'…':''}</div>
                  <div style={s.postMeta}>{platformIcon(p.platform, '#666')} {p.platform} · <Clock size={12} strokeWidth={2.5} style={{verticalAlign:'-1px'}}/> {fmtDate(p.scheduled_at)}</div>
                </div>
                <div style={s.postActions}>
                  <button onClick={()=>publishNow(p.id)} style={s.publishBtn}><Play size={14} strokeWidth={3}/> Publish</button>
                  <button onClick={()=>deletePost(p.id)} style={s.deleteBtn}><Trash2 size={14} strokeWidth={2.5}/></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ALL POSTS TAB */}
        {view === 'posts' && (
          <div style={s.card}>
            <div style={s.cardTitle}><MessageSquare size={20} strokeWidth={2.5}/> All Posts ({postList.length})</div>
            {postList.length === 0 && <EmptyState icon={<Edit3 size={40}/>} text="No posts yet. Create your first post." />}
            {postList.map(p => (
              <div key={p.id} style={s.postRow}>
                <div style={{...s.platformDot, background:platformColor(p.platform)}} />
                <div style={{flex:1,minWidth:0}}>
                  <div style={s.postContent}>{p.content.slice(0,100)}{p.content.length>100?'…':''}</div>
                  <div style={s.postMeta}>{platformIcon(p.platform, '#666')} {p.platform} · <StatusBadge status={p.status} /></div>
                </div>
                <div style={s.postActions}>
                  {p.status === 'draft' && <button onClick={()=>publishNow(p.id)} style={s.publishBtn}><Play size={14} strokeWidth={3}/> Publish</button>}
                  <button onClick={()=>deletePost(p.id)} style={s.deleteBtn}><Trash2 size={14} strokeWidth={2.5}/></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* BEST TIMES TAB */}
        {view === 'best-times' && (
          <div style={s.bestTimesGrid}>
            {Object.entries(bestTimes).map(([platform, times]) => (
              <div key={platform} style={s.card}>
                <div style={{display:'flex', alignItems:'center', gap:8, ...s.cardTitle}}>{platformIcon(platform, '#222')} {platform}</div>
                {times.map((t,i) => (
                  <div key={i} style={{padding:'16px 0', borderBottom: i<times.length-1?'2px solid #222':'none'}}>
                    <div style={{fontSize:16,fontFamily:'"Fredoka",sans-serif',fontWeight:700,color:platformColor(platform)}}>{t.day} at {t.time}</div>
                    <div style={{fontSize:14,fontFamily:'"Nunito",sans-serif',color:'#444',fontWeight:600,marginTop:6,marginBottom:12}}>{t.reason}</div>
                    <button onClick={()=>{
                      const next = nextOccurrence(t.day, t.time);
                      setCompose(c=>({...c,scheduled_at:next}));
                      setView('compose');
                    }} style={s.useSlotBtn}>
                      Use this slot →
                    </button>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({status}){
  const colors={draft:'#666',scheduled:'#FF9F1C',published:'#06D6A0',failed:'#EF476F'};
  return <span style={{color:colors[status]||'#666',fontSize:12,fontWeight:800,fontFamily:'"Fredoka",sans-serif',textTransform:'uppercase',letterSpacing:'0.05em',background:colors[status]+'33',padding:'2px 8px',borderRadius:8,border:`2px solid ${colors[status]}`}}>{status}</span>;
}
function EmptyState({icon,text}){ return <div style={{padding:'40px 0',textAlign:'center',color:'#444'}}><div style={{marginBottom:16, color:'#222'}}>{icon}</div><div style={{fontFamily:'"Fredoka",sans-serif',fontWeight:600,fontSize:16}}>{text}</div></div>; }

function platformColor(p){ return {instagram:'#E1306C',twitter:'#1DA1F2',linkedin:'#0077B5',facebook:'#1877F2'}[p]||'#222'; }

function fmtDate(unix){ if(!unix)return''; return new Date(unix*1000).toLocaleString('en-IN',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}); }
function nextOccurrence(day, time){
  const days=['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const target=days.indexOf(day);
  const now=new Date();
  let d=new Date();
  d.setDate(now.getDate()+((target-now.getDay()+7)%7||7));
  const [h,m]=time.split(':');
  d.setHours(Number(h),Number(m),0,0);
  return d.toISOString().slice(0,16);
}

const s = {
  topbar:{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'24px 32px',borderBottom:'3px solid #222',background:'#FFF'},
  pageTitle:{fontFamily:'"Fredoka",sans-serif',fontSize:26,fontWeight:700,letterSpacing:'-0.5px', color:'#222'},
  pageSub:{fontFamily:'"Nunito",sans-serif',fontSize:15,color:'#666',marginTop:4, fontWeight:600},
  topbarRight:{display:'flex',alignItems:'center',gap:8},
  connectedBadge:{padding:'6px 14px',fontFamily:'"Fredoka",sans-serif',borderRadius:12,border:'2px solid #222',boxShadow:'2px 2px 0px #222',fontSize:13,fontWeight:700},
  warnBadge:{fontFamily:'"Fredoka",sans-serif',fontSize:12,color:'#222',background:'#FFD166',border:'2px solid #222',boxShadow:'2px 2px 0px #222',padding:'8px 16px',borderRadius:12, fontWeight:700},
  tabs:{display:'flex',alignItems:'center',gap:12,borderBottom:'3px solid #222',padding:'0 32px',background:'#FFF'},
  tab:{display:'flex',alignItems:'center',gap:6,background:'transparent',border:'none',borderBottom:'4px solid transparent',color:'#666',padding:'16px 12px',cursor:'pointer',fontSize:15,fontWeight:700,fontFamily:'"Fredoka",sans-serif',transition:'all 0.1s'},
  tabActive:{color:'#222',borderBottomColor:'#FF5C5C'},
  saveMsg:{marginLeft:'auto',color:'#06D6A0',fontSize:14,fontFamily:'"Fredoka",sans-serif',fontWeight:800},
  content:{padding:32},
  composeGrid:{display:'grid',gridTemplateColumns:'1fr 380px',gap:24},
  card:{background:'#FFF',border:'3px solid #222',boxShadow:'6px 6px 0px #222',borderRadius:16,padding:'24px',marginBottom:20},
  cardTitle:{display:'flex',alignItems:'center',gap:8,fontFamily:'"Fredoka",sans-serif',fontSize:18,fontWeight:700,marginBottom:20,color:'#222'},
  row:{display:'flex',gap:16,marginBottom:16},
  label:{display:'block',fontSize:13,fontFamily:'"Fredoka",sans-serif',color:'#222',marginBottom:8,fontWeight:700},
  input:{width:'100%',background:'#FFF',border:'3px solid #222',borderRadius:12,padding:'12px 16px',color:'#222',fontSize:15,fontFamily:'"Nunito",sans-serif',fontWeight:600,boxSizing:'border-box',boxShadow:'none'},
  select:{background:'#FFF',border:'3px solid #222',borderRadius:12,padding:'12px 16px',color:'#222',fontSize:15,fontFamily:'"Nunito",sans-serif',fontWeight:600,width:'100%',appearance:'none'},
  aiBtn:{width:'100%',display:'flex',alignItems:'center',justifyContent:'center',gap:8,background:'#118AB2',border:'3px solid #222',boxShadow:'3px 3px 0px #222',borderRadius:12,padding:'14px',color:'#FFF',fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'"Fredoka",sans-serif',marginTop:8,transition:'all 0.1s'},
  platformRow:{display:'flex',gap:12,marginBottom:20,flexWrap:'wrap'},
  platformBtn:{display:'flex',alignItems:'center',gap:6,background:'#FFF',border:'2px solid #CCC',borderRadius:10,padding:'10px 16px',color:'#666',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:'"Fredoka",sans-serif',transition:'all 0.1s'},
  textarea:{width:'100%',background:'#F6F8FA',border:'3px solid #222',borderRadius:12,padding:'16px',color:'#222',fontSize:15,resize:'vertical',fontFamily:'"Nunito",sans-serif',fontWeight:600,lineHeight:1.6,boxSizing:'border-box'},
  charRow:{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:12},
  improveBtn:{display:'flex',alignItems:'center',gap:6,background:'#FFF',border:'2px solid #222',boxShadow:'2px 2px 0px #222',borderRadius:8,padding:'6px 12px',color:'#222',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'"Fredoka",sans-serif'},
  actionRow:{display:'flex',gap:16,marginTop:24},
  draftBtn:{flex:1,background:'#FFF',border:'3px solid #222',boxShadow:'3px 3px 0px #222',borderRadius:12,padding:'12px',color:'#222',fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'"Fredoka",sans-serif'},
  scheduleBtn:{flex:2,display:'flex',alignItems:'center',justifyContent:'center',gap:8,background:'#FFD166',border:'3px solid #222',boxShadow:'3px 3px 0px #222',borderRadius:12,padding:'12px',color:'#222',fontSize:16,fontWeight:700,cursor:'pointer',fontFamily:'"Fredoka",sans-serif'},
  preview:{background:'#FFF',border:'3px solid #222',borderRadius:12,padding:20,marginTop:8},
  previewHeader:{display:'flex',alignItems:'center',gap:12,marginBottom:16},
  previewAvatar:{width:40,height:40,borderRadius:12,background:'#EF476F',border:'2px solid #222',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,fontWeight:800,fontFamily:'"Fredoka",sans-serif',color:'#FFF'},
  previewContent:{fontSize:15,color:'#222',lineHeight:1.6,whiteSpace:'pre-wrap',wordBreak:'break-word',fontFamily:'"Nunito",sans-serif',fontWeight:600},
  previewTime:{fontSize:13,fontFamily:'"Fredoka",sans-serif',fontWeight:600,color:'#666',marginTop:16,borderTop:'2px dashed #CCC',paddingTop:12},
  ideaItem:{padding:'12px 16px',background:'#FFF',border:'2px solid #222',color:'#222',fontSize:14,fontFamily:'"Nunito",sans-serif',fontWeight:700,cursor:'pointer',borderRadius:10,marginBottom:8,boxShadow:'2px 2px 0px #222',transition:'all 0.1s'},
  postRow:{display:'flex',alignItems:'flex-start',gap:16,padding:'20px 0',borderBottom:'3px solid #222'},
  platformDot:{width:16,height:16,borderRadius:'50%',flexShrink:0,marginTop:4,border:'2px solid #222',boxShadow:'1px 1px 0px #222'},
  postContent:{fontSize:15,fontFamily:'"Nunito",sans-serif',fontWeight:600,color:'#222',lineHeight:1.5,marginBottom:8},
  postMeta:{fontSize:13,fontFamily:'"Fredoka",sans-serif',fontWeight:600,color:'#666',display:'flex',alignItems:'center',gap:6},
  postActions:{display:'flex',gap:12,flexShrink:0},
  publishBtn:{display:'flex',alignItems:'center',gap:6,background:'#06D6A0',border:'2px solid #222',boxShadow:'2px 2px 0px #222',borderRadius:8,padding:'6px 12px',color:'#222',fontSize:13,fontWeight:800,cursor:'pointer',fontFamily:'"Fredoka",sans-serif'},
  deleteBtn:{background:'#FFF',border:'2px solid #222',boxShadow:'2px 2px 0px #222',borderRadius:8,padding:'6px 10px',color:'#EF476F',cursor:'pointer'},
  bestTimesGrid:{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:24},
  useSlotBtn:{display:'block',width:'100%',background:'#FFF',border:'2px solid #222',boxShadow:'2px 2px 0px #222',borderRadius:8,padding:'8px',color:'#222',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:'"Fredoka",sans-serif',textAlign:'center'},
};
